// Radio Button Model - Valtio-based reactive state management
import {proxy, ref} from "valtio";

import {IQueryParamProvider} from "@/shared/contracts";
import {ValidateInterface} from "@/shared/contracts/validate.interface";
import {EventEmitter} from "@/shared/event-emitter";

import {FormFieldEvents, ValidationError} from "./types";

export interface RadioOption<T = string> {
    value: T;
    label: string;
    labelKey?: string; // Translation key
    labelParams?: Record<string, string | number>; // Params for interpolation
    disabled?: boolean;
}

interface RadioButtonConfig<T = string> {
    options: Array<RadioOption<T>>;
    initialValue?: T;
    disabled?: boolean;
    isRequired?: boolean;
    requireMessage?: string;
}

class RadioButtonClass<T = string> implements ValidateInterface, IQueryParamProvider {
    // Reactive state properties
    _value: T | null;
    _disabled: boolean;
    _errors: Array<ValidationError>;

    // Non-reactive properties
    private readonly initialValue: T | null;
    private readonly isRequiredValue: boolean;
    private readonly requireMessage: string;
    public readonly options: Array<RadioOption<T>>;
    public readonly events: EventEmitter<FormFieldEvents>;

    constructor(config: RadioButtonConfig<T>) {
        this.initialValue = config.initialValue ?? null;
        this._value = this.initialValue;
        this._disabled = config.disabled ?? false;
        this._errors = [];
        this.isRequiredValue = config.isRequired ?? false;
        this.requireMessage = config.requireMessage || "Please select an option";
        this.options = config.options;
        this.events = ref(new EventEmitter<FormFieldEvents>()) as EventEmitter<FormFieldEvents>;
    }

    // Getters
    get value(): T | null {
        return this._value;
    }

    get disabled(): boolean {
        return this._disabled;
    }

    get error(): ValidationError | null {
        return this._errors[0] || null;
    }

    get errors(): Array<ValidationError> {
        return this._errors;
    }

    get hasError(): boolean {
        return this._errors.length > 0;
    }

    get shouldShowError(): boolean {
        return this.hasError;
    }

    get isRequired(): boolean {
        return this.isRequiredValue;
    }

    get isValid(): boolean {
        return this.validate().isValid;
    }

    // Setters
    setDisabled(disabled: boolean): void {
        this._disabled = disabled;
    }

    // Check if option is selected
    isSelected(option: RadioOption<T>): boolean {
        return this._value === option.value;
    }

    // Check if option is disabled
    isOptionDisabled(option: RadioOption<T>): boolean {
        return this._disabled || option.disabled || false;
    }

    // Set value
    setValue(value: T): void {
        if (this._disabled) return;

        this._value = value;
        this._errors = [];
        this.events.emit("change", String(value));
    }

    // Validate
    validate(): {isValid: boolean; error?: string} {
        // Simple validation: if required, must have a value
        if (this.isRequiredValue && this._value === null) {
            const error: ValidationError = {
                type: "error",
                message: this.requireMessage,
            };
            this._errors = [error];
            return {isValid: false, error: this.requireMessage};
        }

        this._errors = [];
        return {isValid: true};
    }

    // Reset to initial value
    reset(): void {
        this._value = this.initialValue;
        this._errors = [];
        this.events.emit("change", String(this._value ?? ""));
    }

    // Clear value
    clear(): void {
        this._value = null;
        this._errors = [];
    }

    // Event handlers
    onBlur(): void {
        this.validate();
        this.events.emit("blur");
    }

    onFocus(): void {
        this.events.emit("focus");
    }

    scrollIntoView(): void {
        this.events.emit("scrollIntoView");
    }

    // Query params (IQueryParamProvider implementation)
    getQueryParam(paramName?: string): string {
        if (this._value === null) {
            return "";
        }
        const name = paramName || "value";
        return `${name}=${encodeURIComponent(String(this._value))}`;
    }

    // Set from query params
    setFromQueryParams(params: URLSearchParams, paramName?: string): void {
        const name = paramName || "value";
        const value = params.get(name);

        if (value !== null) {
            // Try to find matching option by converting value to appropriate type
            const matchingOption = this.options.find((opt) => {
                const optValue = opt.value;
                // Handle boolean values
                if (typeof optValue === "boolean") {
                    return optValue === (value === "true");
                }
                // Handle number values
                if (typeof optValue === "number") {
                    return optValue === Number(value);
                }
                // Default: string comparison
                return String(optValue) === value;
            });

            if (matchingOption) {
                this._value = matchingOption.value;
            }
        }
    }
}

// Export type
export type RadioButtonInstance<T = string> = RadioButtonClass<T>;

// Factory function
export function RadioButton<T = string>(config: RadioButtonConfig<T>): RadioButtonInstance<T> {
    return proxy<RadioButtonInstance<T>>(new RadioButtonClass<T>(config));
}
